import type { Viewing, ViewingDog } from '@bbbk/schema';
import type { api as ApiFn } from '../../lib/api';

// TURNING A VIEWING INTO A CUSTOMER — the seam, in one file.
//
// The conversion is a SEQUENCE of writes rather than one endpoint
// (docs/architecture/book-them-in/decision.md, ADR-2), and its two viewing-side
// writes live here so there is ONE WRITER FOR THE ROW'S STATE. The desk pill,
// the modal's dropdown and this path all reach `PATCH /api/viewings/:id`; two
// writers for one piece of state is how a row and a modal come to disagree
// about the same viewing.
//
// NOTHING IS WRITTEN WHEN `Book` IS PRESSED. The prototype set the status on the
// press, which demonstrates a conversion that has not happened and cannot be
// undone by closing the form — AC5 is the rule against it.

/** One dog row as the household form holds it. */
export interface OwnerFormDog {
  name: string;
  breed: string;
  size: string;
  vaccinesDue: string;
}

/** The shape `OwnerForm` accepts to open a create already filled in. */
export interface OwnerCreateDefaults {
  address: string;
  contacts: { name: string; phone: string; email: string }[];
  dogs: OwnerFormDog[];
}

/**
 * WHERE BOOK GOES. A viewing that has no household yet opens the create form,
 * filled in from what the viewing knew. A viewing whose household already
 * exists — the AC5b ending, household saved and booking abandoned — opens a
 * NEW BOOKING for that household instead.
 *
 * It used to open the create form both times, and the story's own walkthrough
 * (save the household, abandon the booking, press Book again) produced two
 * identical households with the booking on the second (review, PR #174). The
 * viewing remembered its booking and never its household; `ownerId` is the
 * missing memory.
 */
export interface BookRoute {
  bookingId: null;
  createOwner?: true;
  ownerId?: string;
  initialMode?: 'new';
  convertingViewing: Viewing;
}

export function bookRoute(v: Viewing): BookRoute {
  return v.ownerId
    ? { bookingId: null, ownerId: v.ownerId, initialMode: 'new', convertingViewing: v }
    : { bookingId: null, createOwner: true, convertingViewing: v };
}

/**
 * What the household form arrives carrying (AC1, AC2).
 *
 * A HOUSEHOLD HAS NO NAME OF ITS OWN — it is known by its first contact,
 * exactly as every other household is. `owners` stores an address and metadata;
 * names live in `owner_contacts`. The prototype carried a household-name field
 * until review caught it.
 *
 * THE NAMELESS ROW IS CARRIED THROUGH, not dropped (AC7b). A viewing may
 * legitimately record a dog with a breed and no name, and `OwnerForm.save` used
 * to discard exactly that row with `if (!d.name.trim()) continue` — silently.
 * It arrives here with its breed intact and an empty name, and the form blocks
 * Save until somebody fills it in. Carried through AND asked for, which is both
 * halves of the criterion.
 *
 * Size and the vaccination date are NOT guessed. A viewing records neither.
 * The size arrives UNCHOSEN and the household form asks for it before Save —
 * AC2's "I add the size" — where the first cut pre-selected Big and every
 * converted Beagle was saved as a big dog in silence (review, PR #174).
 */
export function viewingToOwnerDefaults(v: Viewing): OwnerCreateDefaults {
  const dogs = v.dogs.map((d) => ({
    name: d.name ?? '',
    breed: d.breed ?? '',
    size: '',
    vaccinesDue: '',
  }));
  return {
    // A viewing never records one, and inventing a blank is honest.
    address: '',
    contacts: [{ name: v.contactName, phone: v.phone ?? '', email: '' }],
    // A viewing with no dogs opens one empty row, as a fresh household does.
    dogs: dogs.length ? dogs : [{ name: '', breed: '', size: '', vaccinesDue: '' }],
  };
}

/**
 * The dogs as the household form saved them, in the viewing's own shape — so the
 * name somebody gave the nameless Setter during conversion reaches the viewing
 * too, and the desk row stops reading "Rex, —" about a dog now called Bramble.
 * A wholly blank row says nothing and is not carried.
 */
export function dogsForViewing(dogs: OwnerFormDog[]): ViewingDog[] {
  return dogs
    .filter((d) => d.name.trim() || d.breed.trim())
    .map((d) => ({ name: d.name.trim() || null, breed: d.breed.trim() || null }));
}

/**
 * THE HOUSEHOLD EXISTS — written when the household saves, not when the booking
 * does, and in ONE patch: the visit happened (they came, so `visited` is a
 * consequence rather than a decision), the household it became (`ownerId`, so
 * the next Book press books THEM rather than creating them again), and the dogs
 * as named on the form.
 *
 * Waiting for the booking would leave the AC5b ending — household saved,
 * booking abandoned — with a viewing still reading Expected about a visit that
 * demonstrably happened, and with no way back to the household it made.
 */
export async function recordHousehold(
  api: typeof ApiFn,
  viewingId: string,
  ownerId: string,
  dogs: ViewingDog[],
): Promise<void> {
  await api(`/viewings/${viewingId}`, { method: 'PATCH', body: { status: 'visited', ownerId, dogs } });
}

/**
 * The booking exists — written only once it genuinely does (AC8).
 *
 * The booked marker and the pressable link render from `booking_id` alone, so
 * writing it early is a visible lie about a booking nobody made. The status is
 * already correct by this point and is deliberately not sent again: it would be
 * a second writer for a value nothing changed.
 */
export async function linkBooking(api: typeof ApiFn, viewingId: string, bookingId: string): Promise<void> {
  await api(`/viewings/${viewingId}`, { method: 'PATCH', body: { bookingId } });
}
